import { List, ListItem, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BannerInfo, BannerInfoContainer, TypoContainer } from "./BannerInfoContainer.styles";
import { Progress } from "./Progress";
import { FILTERS } from "../../consts";
import { useAttributeTabStore } from "../../stores/attributeTabStore";
import type { RunsticResponse } from "../../types";
import { checkCustomLogicFe, getEnvironmentVariables } from "../../utils/general";
import { CircularProgress } from "../CircularProgress/CircularProgress.component";

//import { ATTRIBUTE_TRANSLATION_MAP } from "../AgendaWidget/utils";

export type ICourse = {
  id: string;
  nameCourse: string;
  completed: number;
  total: number;
  mandatory: boolean;
}
type NewProgress = {
    attributeName: string,
    attributeId: number | string,
    los: number,
    completedLos: number,
    showCircle?: boolean,
};

export const ProgressContainer = ({ userProgress, translatedAttribute }: { userProgress: RunsticResponse; translatedAttribute: boolean }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { customLogicFe } = getEnvironmentVariables();
  const [hoverCourse, setHoverCourse] = useState<string | number | null>(null);
  const [progressList, setProgressList] = useState<NewProgress[]>([]); //lista degli attributi con relativo cerchio di progressione

  const { selectedTab, setSelectedTab } = useAttributeTabStore();

  useEffect(() => {
    if(userProgress){
      //handleProgressList();
      const attributes = userProgress.attributes?.map((singleAttribute:NewProgress) => {
        return  {
          attributeId:singleAttribute.attributeId,
          attributeName:singleAttribute.attributeName,
          completedLos:singleAttribute.completedLos,
          los:singleAttribute.los,
          showCircle: true
        };
      }) || [];

      // Riordino arrayResult: [attributo posizione-1 admin (solo se presente), mandatory, attributi rimanenti]
      const arrayResult: NewProgress[] = [];

      // Aggiungo il primo attribute solo se è quello con posizione 1 in admin (translatedAttribute)
      if (translatedAttribute && attributes.length > 0) {
        arrayResult.push(attributes[0]);
      }

      // Aggiungo mandatory
      if(userProgress.mandatory && userProgress.mandatory.los > 0 && !checkCustomLogicFe({ envVariable: customLogicFe, strValueToCheck: "hideMandatoryForYou" })) {
        arrayResult.push({
          attributeName: "mandatory",
          attributeId: "mandatory",
          los: userProgress.mandatory.los,
          completedLos: userProgress.mandatory.completedLos,
          showCircle: false
        });
      }

      // Aggiungo i rimanenti attributes: se translatedAttribute, dal secondo in poi; altrimenti tutti
      if (translatedAttribute) {
        if (attributes.length > 1) {
          arrayResult.push(...attributes.slice(1));
        }
      } else {
        arrayResult.push(...attributes);
      }

      // Suggested by AI
      if(userProgress.suggestedByAI && userProgress.suggestedByAI.los > 0) {
        arrayResult.push({
          attributeId: FILTERS.FOR_YOU_AI,
          attributeName: "AI_SUGGESTED",
          completedLos: userProgress.suggestedByAI.completedLos,
          los: userProgress.suggestedByAI.los,
          showCircle: true
        });
      }

      setProgressList(arrayResult); //set list after mandatories check
    }

  }, [userProgress, translatedAttribute, customLogicFe]);

  const calculateProgressPercentage = (progress: number, total: number) => {
    if(!progress || !total) return 0;
    if(total === 0) return 0;

    return (progress / total) * 100;
  };

  const calculateRadius = (index: number) => {
    let radiusMax = 38.4;
    let radius = radiusMax - (index * 6.4);
    let r = radius / 16; //pixel into rem
    let radiusInRem = (r + "rem").toString(); //rem as string
    return { r: radiusInRem, radius };
  };

  const textColorCourse = (mandatory: boolean) =>
    mandatory ? theme.customColors.textWarning : theme.customColors.systemSecondary01;

  const isHover = (courseTagId: string | number | null) => (courseTagId === hoverCourse || selectedTab === courseTagId);
  
  const handleLabel =(label:number | null)=> label ?? "0";
  
  const isProfile = window.location.href.includes("profile");
  const collapsedView = progressList.length >= 6;

  return (
    <div
      aria-label={ t("your_progress") }
      id="progress-container"
    >
      { !collapsedView && (
        <svg viewBox="-15 -45 30 90" height="13.5rem" width={ "100%" } aria-hidden="true">
          { progressList?.map((attribute: NewProgress, index: number) => {
            return (
              <Progress
                key={ attribute.attributeName + "-progression-circular" }
                onHover={ (id) => setHoverCourse(id) }
                progress={
                  calculateProgressPercentage(
                    //@ts-ignore
                    attribute?.completedLos, attribute?.los)
                }
                { ...calculateRadius(index) }
                //@ts-ignore
                courseInfo={ attribute }
                courseId={ attribute.attributeId }
                courseTag={ attribute.attributeName }
                isHover={ isHover }
                accentVisible={ !isProfile || (isProfile && !collapsedView) }
              />
            );
          }) }
        </svg>
      ) }

      <List disablePadding>
        { progressList?.map((attribute:NewProgress) => {
          const isMandatory = attribute.attributeName === "mandatory";
          const isSystemLabel = isMandatory || attribute.attributeName === "AI_SUGGESTED";
          const courseId = `course-${attribute.attributeName}`;
          const courseCompletion = `course-${attribute.attributeName}-completion`;
          const progress = calculateProgressPercentage(
            //@ts-ignore
            attribute?.completedLos, attribute?.los
          );
          return (
            <ListItem
              disablePadding
              aria-roledescription="course name and your progression"
              tabIndex={ 0 }
              key={ attribute.attributeName }
            >
              <BannerInfoContainer
                $isHover={ isHover(attribute.attributeId) || selectedTab === attribute.attributeId }
                $collapsedView={ collapsedView }
                $accentVisible={ !isProfile || (isProfile && !collapsedView) }
                $isProfile={ isProfile }
                onMouseOver={ () => setHoverCourse(attribute.attributeId) }
                onMouseLeave={ () => setHoverCourse(null) }
                onClick={ () => setSelectedTab(attribute.attributeId.toString()) }
              >
                <BannerInfo
                  $collapsedView={ collapsedView }
                  $mandatory={ isMandatory }
                >
                  <TypoContainer
                    $collapsedView={ collapsedView }>
                    <Typography fontSize="0.75rem" fontWeight={ 700 } color={ textColorCourse(isMandatory) }>
                      <span id={ courseId }>
                        { isSystemLabel ? t(attribute.attributeName) : attribute.attributeName }
                      </span>
                    </Typography>
                    <Typography fontSize="0.75rem" color={ theme.customColors.exploreCardText }>
                      <span id={ courseCompletion }>
                        {
                        //@ts-ignore
                          handleLabel(attribute.completedLos)
                        + "/" +
                        //@ts-ignore
                        handleLabel(attribute.los) + " " + t("filters_completed")
                        }
                      </span>
                    </Typography>
                  </TypoContainer>
                </BannerInfo>
                { collapsedView && (
                  <CircularProgress
                    circleColor={ isMandatory ? theme.customColors.textWarning : theme.customColors.systemSecondary01 }
                    hidePercentage={ true }
                    percentageOfCompletion={ `${Math.round(progress)}%` }
                    roundedStroke={ true }
                    thickness={ 6 }
                  />
                ) }
              </BannerInfoContainer>
            </ListItem>
          );
        }) }
      </List>
    </div>
  );
};
